import { Outlet, useNavigate, useRouterState } from '@tanstack/react-router';
import { LogOutIcon, UserIcon } from 'lucide-react';
import {
  AppShell,
  Avatar,
  AvatarFallback,
  BrandMark,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  Separator,
  ThemeToggle,
} from '@opengoal/ui';
import { useTranslation } from 'react-i18next';
import { useNavGroups } from '@/app/navigation';
import { env } from '@/app/env';
import { useCurrentUser, useSignOut } from '@/modules/auth';
import { initials } from '@/shared/lib/format';

/**
 * Chrome for every authenticated screen: sidebar, header, content area.
 *
 * The auth guard lives on the route (see app/routes/roots.tsx), not here — a
 * component-level guard renders once before it redirects, which briefly flashes
 * the app shell to a signed-out user.
 */
export function AppLayout() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const navGroups = useNavGroups();
  const pathname = useRouterState({ select: (state) => state.location.pathname });

  return (
    <AppShell
      brand={<BrandMark name={env.appName} tagline={t('app.tagline')} />}
      brandCollapsed={<BrandMark name={env.appName} markOnly size="sm" />}
      navGroups={navGroups}
      selectedKey={pathname}
      onNavigate={(key) => void navigate({ to: key })}
      headerRight={
        <>
          <ThemeToggle />
          <Separator orientation="vertical" className="mx-1 hidden h-6! sm:block" />
          <UserMenu />
        </>
      }
    >
      <Outlet />
    </AppShell>
  );
}

function UserMenu() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const user = useCurrentUser();
  const signOut = useSignOut();

  const name = user?.name ?? user?.email ?? t('header.account');

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          className="flex items-center gap-2 rounded-full py-0.5 ps-0.5 pe-1 outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50 sm:pe-2"
        >
          <Avatar className="size-9">
            <AvatarFallback className="bg-primary text-[13px] text-primary-foreground">
              {initials(name)}
            </AvatarFallback>
          </Avatar>
          <span className="hidden max-w-40 truncate text-[13px] font-medium sm:block">{name}</span>
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56">
        <DropdownMenuLabel className="grid gap-0.5">
          <span className="truncate">{user?.name ?? t('header.account')}</span>
          {user?.email ? (
            <span className="truncate text-xs font-normal text-muted-foreground">{user.email}</span>
          ) : null}
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuItem onSelect={() => void navigate({ to: '/settings' })}>
          <UserIcon />
          {t('profile.myProfile')}
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem variant="destructive" onSelect={() => void signOut()}>
          <LogOutIcon />
          {t('auth.signOut')}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
